import { BaseModalProps, Modal } from '@/components/Modal';
import { Button } from '@/components/ui';
import { DatePicker } from '@/components/ui/DatePicker';
import {
  endOfDay,
  endOfMonth,
  endOfYear,
  startOfDay,
  startOfMonth,
  startOfYear,
  subMonths,
} from 'date-fns';
import { useState } from 'react';
import { DateRange } from 'react-day-picker';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { movideskApi } from '@/lib/api/movidesk';
import { refreshSyncDates } from '@/lib/sync';
import { useTickets } from '@/hooks/useTickets';

export function SyncTicketsModal({ open, onOpenChange }: BaseModalProps) {
  const { t } = useTranslation();
  const { refreshTickets } = useTickets();
  const [period, setPeriod] = useState<DateRange>();
  const [isLoading, setIsLoading] = useState(false);

  const setCurrentMonth = () => {
    setPeriod({
      from: startOfMonth(new Date()),
      to: endOfMonth(new Date()),
    });
  };

  const setPreviousMonth = () => {
    setPeriod({
      from: startOfMonth(subMonths(new Date(), 1)),
      to: endOfMonth(subMonths(new Date(), 1)),
    });
  };

  const setCurrentYear = () => {
    setPeriod({
      from: startOfYear(new Date()),
      to: endOfYear(new Date()),
    });
  };

  const handleSync = async () => {
    if (!period?.from || !period?.to) {
      toast.error(t('tickets.syncModal.periodRequired'));
      return;
    }

    try {
      setIsLoading(true);

      const syncParams = {
        startDate: startOfDay(period.from).toISOString(),
        endDate: endOfDay(period.to).toISOString(),
      };

      const result = await movideskApi.syncTickets(syncParams);

      // The API answers 200 even when Movidesk fails: surface its errors instead of a false success
      if (result.errors.length > 0) {
        toast.error(`${t('tickets.syncModal.error')}: ${result.errors[0]}`);
      } else {
        toast.success(t('tickets.syncSuccess'));
      }

      await Promise.all([refreshTickets(), refreshSyncDates()]);
      onOpenChange(false);
      setPeriod(undefined);
    } catch (error: any) {
      toast.error(error.message || t('tickets.syncModal.error'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onOpenChange={isLoading ? () => {} : onOpenChange}
      title={t('tickets.syncModal.title')}
      width={500}
    >
      <DatePicker
        label={t('tickets.syncModal.periodLabel')}
        placeholder={t('tickets.syncModal.periodPlaceholder')}
        mode="range"
        value={period}
        onChange={(value) => setPeriod(value as DateRange | undefined)}
      />

      <div className="gap-2xs mt-sm grid grid-cols-3">
        <Button variant="secondary" onClick={setCurrentMonth} disabled={isLoading}>
          {t('tickets.syncModal.currentMonth')}
        </Button>
        <Button variant="secondary" onClick={setPreviousMonth} disabled={isLoading}>
          {t('tickets.syncModal.previousMonth')}
        </Button>
        <Button variant="secondary" onClick={setCurrentYear} disabled={isLoading}>
          {t('tickets.syncModal.currentYear')}
        </Button>
      </div>

      <Button
        className="mt-2xl"
        onClick={handleSync}
        loading={isLoading}
        disabled={!period?.from || !period?.to || isLoading}
      >
        {t('common.sync')}
      </Button>
    </Modal>
  );
}
